/**
 * PulseReactions — iter234
 * ────────────────────────────────────────────────────────────
 * Tiny stateful reactions row (Heart + Clap) for any Pulse event.
 * Anonymous fans get a persistent device id in localStorage so their
 * toggles feel personal without forcing signup. Optimistic UI so taps
 * feel instant; server confirms via `/api/pulse/events/{id}/react`.
 *
 * Usage:
 *   <PulseReactions ev={ev} onUpdate={(updated) => …} size="md" />
 *
 * `onUpdate(updatedEvent)` is optional — lets the parent list keep
 * counts in sync when the same event is visible in multiple places.
 */
import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEVICE_KEY = 'dl_pulse_device';

/** Generate/read a stable per-browser device id — anonymous, opaque. */
export const getPulseDeviceId = () => {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id) {
      // Simple UUID-ish token — crypto.randomUUID when available.
      id = (window.crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2, 10)));
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};

/** Small emoji-inline clap icon — matches Heart's stroke visual weight. */
const ClapIcon = ({ className = '', filled = false }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 14v-3a2 2 0 0 1 4 0v3" />
    <path d="M12 14V8a2 2 0 0 1 4 0v6" />
    <path d="M16 14V9.5a2 2 0 0 1 4 0V15c0 3.3-2.7 6-6 6h-1a6 6 0 0 1-6-6v-1l-2-3a1.5 1.5 0 0 1 2.5-1.6L10 12" />
    <path d="M6 4l1 2M10 3l.5 2M14 3l-.5 2" />
  </svg>
);

const SIZE_MAP = {
  sm: { btn: 'px-2 py-1 text-[11px]', icon: 'w-3.5 h-3.5' },
  md: { btn: 'px-2.5 py-1.5 text-xs',  icon: 'w-4 h-4' },
  lg: { btn: 'px-3 py-2 text-sm',      icon: 'w-4.5 h-4.5' },
};

const ReactionButton = ({ kind, Icon, label, active, count, popping, sizeCls, evId, onToggle }) => {
  const isHeart = kind === 'heart';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(kind); }}
      aria-label={active ? `Remove ${label}` : `Give a ${label}`}
      data-testid={`pulse-react-${kind}-${evId || 'x'}`}
      className={`inline-flex items-center gap-1.5 rounded-full ring-1 transition select-none active:scale-95 ${sizeCls.btn} ${
        active
          ? 'bg-rose-500/20 ring-rose-400/50 text-rose-200'
          : 'bg-white/[0.05] ring-white/15 text-white/70 hover:bg-white/[0.10] hover:text-white'
      }`}
    >
      <span className={`relative inline-flex ${popping ? 'animate-[reactPop_380ms_ease-out]' : ''}`}>
        {isHeart ? (
          <Icon
            className={sizeCls.icon}
            fill={active ? 'currentColor' : 'none'}
            strokeWidth={active ? 0 : 2}
          />
        ) : (
          <Icon className={sizeCls.icon} filled={active} />
        )}
      </span>
      {count > 0 && (
        <span className="font-mono tabular-nums text-[11px] font-bold">
          {count > 999 ? `${(count / 1000).toFixed(1)}k` : count}
        </span>
      )}
    </button>
  );
};

const PulseReactions = ({ ev, onUpdate, size = 'md', className = '' }) => {
  const [counts, setCounts] = useState({
    heart: ev?.reactions?.heart || 0,
    clap:  ev?.reactions?.clap  || 0,
  });
  const [mine, setMine] = useState(new Set(ev?.my_reactions || []));
  const [busy, setBusy] = useState(false);
  const [pop, setPop] = useState(null); // 'heart' | 'clap' | null (for tiny pop-in on tap)

  const sizeCls = SIZE_MAP[size] || SIZE_MAP.md;
  const deviceId = useMemo(() => getPulseDeviceId(), []);

  const toggle = useCallback(async (kind) => {
    if (busy || !ev?.id) return;
    // Optimistic update.
    const alreadyMine = mine.has(kind);
    const nextMine = new Set(mine);
    const nextCounts = { ...counts };
    if (alreadyMine) {
      nextMine.delete(kind);
      nextCounts[kind] = Math.max(0, (nextCounts[kind] || 0) - 1);
    } else {
      nextMine.add(kind);
      nextCounts[kind] = (nextCounts[kind] || 0) + 1;
      setPop(kind);
      setTimeout(() => setPop(null), 380);
    }
    setMine(nextMine);
    setCounts(nextCounts);
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/pulse/events/${ev.id}/react`,
        { kind, device_id: deviceId },
        {
          withCredentials: true,
          headers: { 'X-Pulse-Device': deviceId },
        },
      );
      const serverCounts = r.data?.counts || nextCounts;
      const serverMine = new Set(r.data?.my_reactions || []);
      setCounts(serverCounts);
      setMine(serverMine);
      onUpdate?.({
        ...ev,
        reactions: serverCounts,
        my_reactions: Array.from(serverMine),
      });
    } catch {
      // Revert on error.
      setMine(mine);
      setCounts(counts);
    } finally {
      setBusy(false);
    }
  }, [busy, counts, deviceId, ev, mine, onUpdate]);

  return (
    <>
      <style>{`
        @keyframes reactPop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.35); }
          70%  { transform: scale(0.92); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div className={`inline-flex items-center gap-1.5 ${className}`} data-testid="pulse-reactions">
        <ReactionButton
          kind="heart"
          Icon={Heart}
          label="heart"
          active={mine.has('heart')}
          count={counts.heart || 0}
          popping={pop === 'heart'}
          sizeCls={sizeCls}
          evId={ev?.id}
          onToggle={toggle}
        />
        <ReactionButton
          kind="clap"
          Icon={ClapIcon}
          label="clap"
          active={mine.has('clap')}
          count={counts.clap || 0}
          popping={pop === 'clap'}
          sizeCls={sizeCls}
          evId={ev?.id}
          onToggle={toggle}
        />
      </div>
    </>
  );
};

export default PulseReactions;
